/**
 * Voice Command Parser
 * Natural language processing for voice commands
 */

const SPOKEN_NUMBER_TOKENS = [
  'zero',
  'one',
  'two',
  'three',
  'four',
  'five',
  'six',
  'seven',
  'eight',
  'nine',
  'ten',
  'eleven',
  'twelve',
  'oh',
  'o',
];

const SPOKEN_NUMBER_MAP: Record<string, string> = {
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
  eleven: '11',
  twelve: '12',
  oh: '0',
  o: '0',
};

const SPOKEN_NUMBER_SEQUENCE = new RegExp(`^(?:${SPOKEN_NUMBER_TOKENS.join('|')})(?:[\s-]+(?:${SPOKEN_NUMBER_TOKENS.join('|')}))*$`, 'i');

const convertSpokenNumberToDigits = (input: string): string => {
  const normalized = input
    .toLowerCase()
    .replace(/-/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  if (!normalized) return '';

  const tokens = normalized.split(' ');
  const results: string[] = [];

  tokens.forEach(token => {
    if (SPOKEN_NUMBER_MAP[token] !== undefined) {
      results.push(SPOKEN_NUMBER_MAP[token]);
    }
  });

  return results.join('');
};

const normalizeWellIdentifier = (value: string): string => {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;

  if (SPOKEN_NUMBER_SEQUENCE.test(trimmed)) {
    const digits = convertSpokenNumberToDigits(trimmed);
    return digits || trimmed;
  }

  return trimmed;
};

export interface ParsedCommand {
  intent: string;
  action: string;
  parameters: Record<string, any>;
  confidence: number;
  originalText: string;
}

export interface CommandParserOptions {
  locale?: string;
  context?: CommandContext;
}

export interface CommandContext {
  currentWellId?: string;
  currentPage?: string;
  lastCommands?: ParsedCommand[];
}

export type CommandIntent =
  | 'navigate'
  | 'search'
  | 'create'
  | 'show'
  | 'record'
  | 'update'
  | 'help'
  | 'unknown';

/**
 * Function signature for extracting parameters from a matched command
 */
type CommandExtractor = (
  match: RegExpMatchArray,
  text: string,
  context?: CommandContext
) => Record<string, any>;

interface CommandPattern {
  intent: CommandIntent;
  action: string;
  patterns: RegExp[];
  extractor?: CommandExtractor;
}

interface CommandIntentPatterns extends CommandPattern {}

class VoiceCommandParser {
  private localeCache = new Map<string, CommandIntentPatterns[]>();

  private patterns: CommandPattern[] = [
    // Navigation commands - ENHANCED
    {
      intent: 'navigate',
      action: 'go_to_well',
      patterns: [
        /(?:navigate to|go to|open|show me|take me to)\s+well\s+(\d+)/i,
        /(?:navigate to|go to|open|show me|take me to)\s+well\s+([a-z]+[\-\s]*\d+)/i,
        /(?:navigate to|go to|open|show me|take me to)\s+(?:well\s+)?((?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|oh|o)(?:[\s-]+(?:zero|one|two|three|four|five|six|seven|eight|nine|ten|eleven|twelve|oh|o))*)/i,
        /well\s+(\d+)/i, // Simple "well 042"
        /well\s+([a-z]+[\-\s]*\d+)/i, // Simple "well alpha-5"
        /(open|show)\s+(?:well\s+)?(\d{1,4})/i,
      ],
      extractor: (match) => ({ wellId: normalizeWellIdentifier(match[match.length - 1]) }),
    },
    {
      intent: 'navigate',
      action: 'go_to_page',
      patterns: [
        /(?:navigate to|go to|open|show me|take me to)\s+(dashboard|analytics|routes|map|tickets|settings)/i,
        /show\s+(dashboard|analytics|routes|map|tickets|settings)/i,
        /open\s+(dashboard|analytics|routes|map|tickets|settings)/i,
      ],
      extractor: (match) => ({ page: match[1].toLowerCase() }),
    },
    {
      intent: 'navigate',
      action: 'show_directions',
      patterns: [
        /(?:show|give me|display)\s+(?:directions|turn by turn|navigation)\s+(?:to\s+)?well\s+(\d+)/i,
        /(?:how do I get to|navigate to)\s+well\s+(\d+)/i,
      ],
      extractor: (match) => ({ wellId: match[1] }),
    },

    // Search/Show commands
    {
      intent: 'show',
      action: 'show_wells_by_status',
      patterns: [
        /show\s+(?:me\s+)?(?:all\s+)?wells?\s+(?:with|that have)\s+(.+)/i,
        /show\s+(?:me\s+)?(?:all\s+)?(.+)\s+wells?/i,
      ],
      extractor: (match, text) => {
        const criteria = match[1].toLowerCase();
        if (criteria.includes('production drop') || criteria.includes('declining')) {
          return { filter: 'declining_production' };
        }
        if (criteria.includes('high priority') || criteria.includes('urgent')) {
          return { filter: 'high_priority' };
        }
        if (criteria.includes('overdue') || criteria.includes('need check')) {
          return { filter: 'overdue' };
        }
        if (criteria.includes('issue') || criteria.includes('problem')) {
          return { filter: 'has_issues' };
        }
        return { filter: 'all', criteria };
      },
    },
    {
      intent: 'show',
      action: 'show_schedule',
      patterns: [
        /(?:what's|show me|display)\s+(?:my\s+)?schedule\s+(?:for\s+)?(today|tomorrow|this week)?/i,
        /muestrame\s+(?:mi\s+)?agenda\s+(?:de\s+)?(hoy|manana|esta semana)?/i,
      ],
      extractor: (match) => ({ timeframe: match[1] || 'today' }),
    },
    {
      intent: 'show',
      action: 'show_statistics',
      patterns: [
        /show\s+(?:me\s+)?(?:production\s+)?(?:stats|statistics)\s+for\s+well\s+(\d+)/i,
      ],
      extractor: (match) => ({ wellId: match[1] }),
    },

    // Create commands
    {
      intent: 'create',
      action: 'create_ticket',
      patterns: [
        /create\s+(?:a\s+)?(?:maintenance\s+)?(?:ticket|task)\s+for\s+well\s+(\d+)/i,
        /(?:add|create)\s+(?:maintenance\s+)?(?:ticket|task)\s+(?:for\s+)?well\s+([a-z]+[\-\s]*\d+)/i,
      ],
      extractor: (match) => ({ wellId: match[1], type: 'maintenance' }),
    },
    {
      intent: 'create',
      action: 'create_issue',
      patterns: [
        /(?:record|create|log)\s+(?:an?\s+)?issue:?\s+(.+)/i,
        /(?:record|create|log)\s+(?:that\s+)?(.+)/i,
      ],
      extractor: (match) => ({ description: match[1].trim() }),
    },
    {
      intent: 'create',
      action: 'add_note',
      patterns: [
        /(?:add|create)\s+(?:a\s+)?note\s+(?:for\s+well\s+)?(\d+):?\s+(.+)/i,
      ],
      extractor: (match) => ({ wellId: match[1], note: match[2].trim() }),
    },
    {
      intent: 'create',
      action: 'start_data_entry',
      patterns: [
        /(?:add|create|enter)\s+(?:new\s+)?(?:data|entry|reading)\s+(?:for\s+)?well\s+(\d+)/i,
        /(?:record|log)\s+(?:production\s+)?(?:data|reading)\s+(?:for\s+)?well\s+(\d+)/i,
      ],
      extractor: (match) => ({ wellId: match[1] }),
    },

    // Search commands
    {
      intent: 'search',
      action: 'search_wells',
      patterns: [
        /(?:search|find)\s+(?:for\s+)?well\s+(.+)/i,
      ],
      extractor: (match) => ({ query: match[1].trim() }),
    },

    // Satellite view commands
    {
      intent: 'navigate',
      action: 'show_satellite_view',
      patterns: [
        /(?:show|display|open)\s+(?:me\s+)?(?:the\s+)?satellite\s+(?:view|image|map)/i,
        /(?:show|view)\s+(?:the\s+)?satellite/i,
        /(?:how do I|where is|help find)\s+(?:this\s+)?(?:well|location)/i,
        /(?:show|display)\s+(?:satellite\s+)?directions/i,
      ],
      extractor: (match, text, context) => ({ 
        wellId: context?.currentWellId || null 
      }),
    },
    {
      intent: 'navigate',
      action: 'show_satellite_for_well',
      patterns: [
        /(?:show|display|open)\s+(?:me\s+)?(?:the\s+)?satellite\s+(?:view|image|map)\s+(?:for\s+)?well\s+(\d+)/i,
        /(?:show|display|open)\s+(?:me\s+)?(?:the\s+)?satellite\s+(?:view|image|map)\s+(?:for\s+)?well\s+([a-z]+[\-\s]*\d+)/i,
        /well\s+(\d+)\s+satellite/i,
      ],
      extractor: (match) => ({ wellId: match[1] }),
    },

    // Update commands
    {
      intent: 'update',
      action: 'update_status',
      patterns: [
        /(?:update|change|set)\s+well\s+(\d+)\s+status\s+to\s+(\w+)/i,
      ],
      extractor: (match) => ({ wellId: match[1], status: match[2] }),
    },

    // Record issue
    {
      intent: 'record',
      action: 'record_issue',
      patterns: [
        /record\s+issue:?\s+(.+)/i,
      ],
      extractor: (match) => ({ description: match[1].trim() }),
    },

    // Time Clock Commands - ENTERPRISE ACTIONS
    {
      intent: 'create',
      action: 'clock_in',
      patterns: [
        /(?:clock|sign|punch)\s+(?:me\s+)?in/i,
        /(?:start|begin)\s+(?:my\s+)?(?:shift|day|work)/i,
        /i(?:'m|m)\s+(?:here|at work|starting|on site)/i,
        /(?:record|log)\s+(?:my\s+)?(?:start|arrival|clock[\s-]?in)/i,
        /(?:check|clock)[\s-]?in/i,
      ],
      extractor: () => ({}),
    },
    {
      intent: 'create',
      action: 'clock_out',
      patterns: [
        /(?:clock|sign|punch)\s+(?:me\s+)?out/i,
        /(?:end|finish)\s+(?:my\s+)?(?:shift|day|work)/i,
        /i(?:'m|m)\s+(?:done|leaving|finished|heading out)/i,
        /(?:record|log)\s+(?:my\s+)?(?:end|departure|clock[\s-]?out)/i,
        /(?:check|clock)[\s-]?out/i,
      ],
      extractor: () => ({}),
    },
    {
      intent: 'create',
      action: 'start_break',
      patterns: [
        /(?:start|take|begin)\s+(?:a\s+)?(?:my\s+)?break/i,
        /(?:going|gonna|gotta)\s+(?:on|take)\s+(?:a\s+)?break/i,
        /(?:break|lunch)\s+(?:time|now)/i,
        /i(?:'m|m)\s+(?:taking|on)\s+(?:a\s+)?break/i,
      ],
      extractor: () => ({}),
    },
    {
      intent: 'create',
      action: 'end_break',
      patterns: [
        /(?:end|finish|stop)\s+(?:my\s+)?break/i,
        /(?:back|returned)\s+from\s+break/i,
        /break\s+(?:over|done|finished)/i,
        /i(?:'m|m)\s+back(?:\s+from\s+break)?/i,
      ],
      extractor: () => ({}),
    },

    // Emergency & Safety Commands
    {
      intent: 'create',
      action: 'emergency_stop',
      patterns: [
        /emergency\s+(?:stop|shutdown|halt)/i,
        /(?:stop|halt)\s+(?:everything|all|operations)/i,
        /(?:red|panic)\s+button/i,
        /(?:immediate|urgent)\s+(?:stop|shutdown)/i,
        /shut\s+(?:it\s+)?down\s+(?:now|immediately)/i,
      ],
      extractor: () => ({ priority: 'critical' }),
    },
    {
      intent: 'create',
      action: 'report_hazard',
      patterns: [
        /(?:report|log|record)\s+(?:a\s+)?(?:hazard|danger|safety issue)/i,
        /(?:there(?:'s| is)\s+)?(?:a\s+)?(?:hazard|danger|leak|spill)/i,
        /(?:safety|hazard)\s+(?:report|alert)/i,
        /(?:gas|oil|chemical)\s+(?:leak|spill)/i,
      ],
      extractor: (match, text) => ({ description: text }),
    },

    // Status & Production Commands
    {
      intent: 'show',
      action: 'check_status',
      patterns: [
        /(?:check|show|what(?:'s| is))\s+(?:my\s+)?(?:the\s+)?status/i,
        /(?:what's|whats|what is)\s+(?:the\s+)?(?:current\s+)?(?:status|situation)/i,
        /(?:how|where)\s+(?:am\s+i|are\s+we|do\s+i\s+stand)/i,
        /(?:current|today's|my)\s+(?:status|progress|stats)/i,
      ],
      extractor: () => ({}),
    },
    {
      intent: 'show',
      action: 'show_production',
      patterns: [
        /(?:show|display|what(?:'s| is))\s+(?:my\s+)?(?:the\s+)?production/i,
        /(?:production|output)\s+(?:numbers|data|stats|statistics)/i,
        /(?:how much|how many)\s+(?:barrels|bbl|oil|gas|water)/i,
        /(?:today's|current|latest)\s+production/i,
      ],
      extractor: () => ({}),
    },

    // Navigation with voice
    {
      intent: 'navigate',
      action: 'start_navigation',
      patterns: [
        /(?:start|begin|give me)\s+(?:voice\s+)?navigation(?:\s+to\s+well\s+(\d+))?/i,
        /(?:navigate|direct)\s+me\s+to\s+well\s+(\d+)/i,
        /(?:take|get)\s+me\s+to\s+well\s+(\d+)/i,
        /(?:turn[\s-]?by[\s-]?turn|voice\s+directions?)(?:\s+to\s+well\s+(\d+))?/i,
      ],
      extractor: (match) => ({ wellId: match[1] || null }),
    },
    {
      intent: 'navigate',
      action: 'stop_navigation',
      patterns: [
        /(?:stop|end|cancel|quit)\s+(?:voice\s+)?navigation/i,
        /(?:no\s+more|stop)\s+(?:directions|navigating)/i,
        /(?:i(?:'m|m)\s+)?(?:here|arrived|at the well)/i,
        /(?:cancel|exit)\s+(?:route|directions)/i,
      ],
      extractor: () => ({}),
    },

    // Mark Complete / Task Management
    {
      intent: 'update',
      action: 'mark_complete',
      patterns: [
        /(?:mark|set)\s+(?:as\s+)?(?:complete|done|finished)/i,
        /(?:complete|finish|done\s+with)\s+(?:well\s+)?(\d+)?/i,
        /(?:well\s+)?(\d+)\s+(?:is\s+)?(?:done|complete|finished)/i,
        /(?:finished|done|completed)\s+(?:checking\s+)?(?:well\s+)?(\d+)?/i,
      ],
      extractor: (match) => ({ wellId: match[1] || null }),
    },

    // Communication
    {
      intent: 'create',
      action: 'call_supervisor',
      patterns: [
        /(?:call|contact|reach|get)\s+(?:my\s+)?(?:supervisor|manager|boss)/i,
        /(?:i\s+)?need\s+(?:to\s+)?(?:talk|speak)\s+(?:to|with)\s+(?:supervisor|manager)/i,
        /(?:connect|patch)\s+me\s+(?:to|with)\s+(?:supervisor|manager)/i,
      ],
      extractor: () => ({}),
    },

    // Help
    {
      intent: 'help',
      action: 'show_help',
      patterns: [
        /(?:help|what can you do|commands)/i,
      ],
    },
  ];

  parse(text: string, options: CommandParserOptions = {}): ParsedCommand {
    const normalizedText = text.trim();
    const locale = options.locale ?? 'en-US';
    const language = locale.startsWith('es') ? 'es' : 'en';
    const localizedPatterns = this.getLocalizedPatterns(language);
    const context = options.context;

    // Try to match against patterns
    for (const command of localizedPatterns) {
      for (const pattern of command.patterns) {
        const match = normalizedText.match(pattern);
        if (match) {
          const parameters = command.extractor 
            ? command.extractor(match, normalizedText, context) 
            : {};
          
          return {
            intent: command.intent,
            action: command.action,
            parameters,
            confidence: 0.9, // High confidence for pattern match
            originalText: text,
          };
        }
      }
    }

    // No match found - try fuzzy matching with context
    const fuzzyResult = this.fuzzyMatch(normalizedText, language, options.context);
    if (fuzzyResult) {
      return fuzzyResult;
    }

    // Unknown command
    return {
      intent: 'unknown',
      action: 'unknown',
      parameters: {},
      confidence: 0,
      originalText: text,
    };
  }

  private fuzzyMatch(
    text: string,
    language: 'en' | 'es',
    context?: CommandParserOptions['context']
  ): ParsedCommand | null {
    const lowerText = text.toLowerCase();
    const wellKeyword = language === 'es' ? 'pozo' : 'well';

    // Check for well numbers
    const wellMatch = lowerText.match(/\b(\d{1,4})\b/);
    if (wellMatch) {
      const wellId = wellMatch[1];
      
      if (lowerText.includes('go') || lowerText.includes('show') || lowerText.includes('open')) {
        return {
          intent: 'navigate',
          action: 'go_to_well',
          parameters: { wellId },
          confidence: 0.7,
          originalText: text,
        };
      }
      
      if (lowerText.includes('data') || lowerText.includes('entry') || lowerText.includes('reading')) {
        return {
          intent: 'create',
          action: 'start_data_entry',
          parameters: { wellId },
          confidence: 0.7,
          originalText: text,
        };
      }
    }

    // Check for general navigation
    if (lowerText.includes('dashboard') || (language === 'es' && lowerText.includes('tablero'))) {
      return {
        intent: 'navigate',
        action: 'go_to_page',
        parameters: { page: 'dashboard' },
        confidence: 0.7,
        originalText: text,
      };
    }

    if (lowerText.includes('analytics') || lowerText.includes('analysis') || lowerText.includes('analitica')) {
      return {
        intent: 'navigate',
        action: 'go_to_page',
        parameters: { page: 'analytics' },
        confidence: 0.7,
        originalText: text,
      };
    }

    if (lowerText.includes('route') || lowerText.includes('map') || lowerText.includes('ruta')) {
      return {
        intent: 'navigate',
        action: 'go_to_page',
        parameters: { page: 'routes' },
        confidence: 0.7,
        originalText: text,
      };
    }

    // Check for schedule queries
    if (lowerText.includes('schedule') || lowerText.includes('agenda')) {
      return {
        intent: 'show',
        action: 'show_schedule',
        parameters: { timeframe: 'today' },
        confidence: 0.7,
        originalText: text,
      };
    }

    if (context?.currentWellId && /this (well|pozo)|current well/.test(lowerText)) {
      if (lowerText.includes('data')) {
        return {
          intent: 'create',
          action: 'start_data_entry',
          parameters: { wellId: context.currentWellId },
          confidence: 0.65,
          originalText: text,
        };
      }
      if (lowerText.includes('issue') || lowerText.includes('problema')) {
        return {
          intent: 'record',
          action: 'record_issue',
          parameters: { wellId: context.currentWellId, description: text },
          confidence: 0.6,
          originalText: text,
        };
      }
    }

    return null;
  }

  getHelpCommands(locale: 'en' | 'es' = 'en'): Array<{ command: string; description: string; examples: string[] }> {
    if (locale === 'es') {
      return [
        {
          command: 'Navegación',
          description: 'Moverse entre pozos o páginas',
          examples: ['Ve al Pozo 042', 'Abre el tablero', 'Muestra rutas'],
        },
        {
          command: 'Datos',
          description: 'Capturar producción y notas',
          examples: ['Agrega datos al Pozo 042', 'Registra presión alta'],
        },
        {
          command: 'Alertas',
          description: 'Revisar y confirmar alertas',
          examples: ['Muestra alertas críticas', 'Reconoce alertas abiertas'],
        },
      ];
    }

    return [
      {
        command: 'Navigation',
        description: 'Navigate to wells or pages',
        examples: [
          'Navigate to Well 042',
          'Go to dashboard',
          'Open analytics',
          'Show me Well 015',
        ],
      },
      {
        command: 'Search & Filter',
        description: 'Find and filter wells',
        examples: [
          'Show me wells with production drops',
          'Show high priority wells',
          'Show overdue wells',
          'Find well 042',
        ],
      },
      {
        command: 'Data Entry',
        description: 'Add production data',
        examples: [
          'Add data for Well 042',
          'Enter reading for Well 015',
          'Record production data for Well 023',
        ],
      },
      {
        command: 'Tasks & Issues',
        description: 'Create tasks and log issues',
        examples: [
          'Create maintenance ticket for Well 015',
          'Record issue: pump making noise',
          'Add note for Well 042: check valve',
        ],
      },
      {
        command: 'Schedule',
        description: 'View your schedule',
        examples: [
          "What's my schedule today?",
          'Show me my schedule for this week',
          'Display my schedule',
        ],
      },
    ];
  }

  getSuggestions(partialText: string, locale: 'en' | 'es' = 'en'): string[] {
    const suggestions: string[] = [];
    const lowerText = partialText.toLowerCase();

    if (lowerText.includes('show') || lowerText.includes('display') || lowerText.includes('muestra')) {
      suggestions.push(
        'Show me wells with production drops',
        'Show high priority wells',
        'Show my schedule today'
      );
    }

    if (lowerText.includes('navigate') || lowerText.includes('go') || lowerText.includes('ve a')) {
      suggestions.push(
        'Navigate to Well 042',
        'Go to dashboard',
        'Go to analytics'
      );
    }

    if (lowerText.includes('create') || lowerText.includes('add') || lowerText.includes('crea')) {
      suggestions.push(
        'Create maintenance ticket for Well 015',
        'Add data for Well 042',
        'Add note for Well 023'
      );
    }

    if (lowerText.includes('record') || lowerText.includes('registra')) {
      suggestions.push(
        'Record issue: pump making noise',
        'Record production data for Well 042'
      );
    }

    // Default suggestions if nothing matches
    if (suggestions.length === 0) {
      if (locale === 'es') {
        suggestions.push(
          'Ve al Pozo 042',
          'Muestrame pozos con problemas',
          'Crea ticket de mantenimiento',
          'Registra datos de producción'
        );
      } else {
        suggestions.push(
          'Navigate to Well 042',
          'Show me wells with issues',
          "What's my schedule today?",
          'Create maintenance ticket',
          'Record issue'
        );
      }
    }

    return suggestions.slice(0, 5);
  }

  private getLocalizedPatterns(language: 'en' | 'es'): CommandIntentPatterns[] {
    if (this.localeCache.has(language)) {
      return this.localeCache.get(language)!;
    }

    const navigateSynonyms = language === 'es'
      ? ['ir a', 've a', 'abre', 'muestrame']
      : ['navigate to', 'go to', 'open', 'show me'];

    const wellWord = language === 'es' ? 'pozo' : 'well';

    const localized: CommandIntentPatterns[] = [
      {
        intent: 'navigate',
        action: 'go_to_well',
        patterns: navigateSynonyms.map(
          (phrase) => new RegExp(`${phrase}\\s+${wellWord}\\s+([a-z0-9\\-]+)`, 'i')
        ),
        extractor: (match) => ({ wellId: match[1] }),
      },
      {
        intent: 'navigate',
        action: 'go_to_page',
        patterns: [
          new RegExp(`${navigateSynonyms.join('|')}\\s+(dashboard|analytics|routes|map|tablero|analiticas|rutas)`, 'i'),
        ],
        extractor: (match) => ({ page: match[1].toLowerCase() }),
      },
      ...this.patterns,
      {
        intent: 'show',
        action: 'show_alerts',
        patterns: [
          /show (?:critical|open) alerts?/i,
          /muestra alertas/i,
        ],
      },
      {
        intent: 'navigate',
        action: 'optimize_route',
        patterns: [
          /optimize route/i,
          /recalcula ruta/i,
        ],
      },
      {
        intent: 'record',
        action: 'acknowledge_alert',
        patterns: [
          /acknowledge alert/i,
          /reconoce alerta/i,
        ],
      },
      {
        intent: 'help',
        action: 'start_voice_training',
        patterns: [
          /train (?:my )?voice/i,
          /entrena mi voz/i,
        ],
      },
    ];

    this.localeCache.set(language, localized);
    return localized;
  }
}

export const commandParser = new VoiceCommandParser();
